import { HttpRetryConfig } from './../../app/config/config.type';
import { AppConfigService } from 'src/app/config/config.service';
import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { concatMap, delay, retryWhen } from 'rxjs/operators';
import { ToastService } from '@cudirect/shared-ui-library';

/**
 * This retry interceptor solves an immediate issue where we are seeeing transient failures for certain endpoints (ex. DDMS, APIM, etc)
 * However, it is best practices to include such a retry interceptor in frontend applications.
 * Returning of(error) will cause the interceptor to loop around again, as if it was the original request.
 * Returning throw(error) will cause the interceptor to resolve / default behavior.
 *
 * TODO:
 * - More graceful ui/ux implementation of error messaging (toast should persist until retry attempts have completed)
 * - Spike to see if we can solve eslint-disable-next-line @typescript-eslint/no-shadow
 * - Exponential backoff (not a flat retry duration)
 */
@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  // Injector is required due to potential circular dependency
  constructor(private inj: Injector) {}

  /** Applicaiton config for overrides (if any), none by default */
  private retryConfig: HttpRetryConfig;

  /** Master switch - is retry enabled */
  private retryEnabled = false;

  /** The number of times to attempt retry */
  private retryAttempts = 3;

  /** The delay between each retryAttempt in milliseconds */
  private retryDelayMs = 2500;

  /** The status codes to trigger retry */
  private retryStatusCodes: string[] = ['0', '400', '500'];

  /** Partial or full routes that *should* be retried */
  private retryIncludedRoutes: string[] = ['cpapi'];

  /** InnerHTML to put in the triggered toast on retry */
  private retryToastMessage = `<span class="bl-font-color--white">Retrying...</span>`;

  /** should not retry for excluded routes */
  private excludedRoutes: string[] = [
    'integration/GeneratePasscode',
    'integration/ValidatePasscode',
    'InstaTouch/CreatePartialApp',
    'SubmitOLAApplication/Check',
    'account/logout',
    'redirect/getPrefillOtuTokenData'
  ];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    /** Handled excluded url list for insta touch validate passcode issue, we can add more urls in excluded list. */
    if (this.excludedRoutes.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      retryWhen((error) =>
        error.pipe(
          // eslint-disable-next-line @typescript-eslint/no-shadow
          concatMap((error, count) => this.retryContent(error, count, request)),
          delay(this.retryDelayMs)
        )
      )
    );
  }

  /**
   * Primary logic function for taking action on error
   * @param error the error contained within the http request
   * @param count the internal framework count of retries
   * @returns throwError(error) for normal behavior or of(error) to trigger retry
   */
  private retryContent(error: HttpErrorResponse, count: number, request: HttpRequest<unknown>): Observable<unknown> {
    if (!this.retryConfig) {
      // If we don't have retry configs, then we need to fetch them
      this.retryConfig = this.inj.get(AppConfigService).getData().httpRetryConfig;

      // Without an Appconfig, we can't safely retry. Without it being enabled (or missing), we won't retry.
      if (!this.retryConfig) return throwError(error);
      else this.mapHttpRetryConfig(this.retryConfig);
    }

    // Check master switch
    if (!this.retryEnabled) return throwError(error);

    // Is this HTTP request url in the list of routes?
    if (!this.retryIncludedRoutes.some((route) => request.url.includes(route))) return throwError(error);

    // Are we out of retry attempts? (retry count starts at 0)
    if (count >= this.retryAttempts) return throwError(error);

    // Does this HTTP error status code match the list of status codes?
    if (!this.retryStatusCodes.includes(error.status.toString())) return throwError(error);

    /** IF ABOVE CONDITIONS ARE SATISFIED - ENTER RETRY LOOP */
    // Only open toast if we have a message, this allows it to be hidden, -50ms for fade animation
    if (this.retryToastMessage) this.inj.get(ToastService).open(this.retryToastMessage, this.retryDelayMs - 100);

    // Return an observable that will trigger the retryWhen pipe, plus some logging
    console.error(`Retrying HTTP request: ${request.url}... (attempt: ${count + 1})`);
    return of(error);
  }

  /**
   * Overrides w/ values that exist in the config, or sets defaults
   * @param config the application config
   */
  private mapHttpRetryConfig(config: HttpRetryConfig): void {
    this.retryEnabled = this.isTruthy(config?.enabled); // Master switch
    if (this.isTruthy(config?.attempts)) this.retryAttempts = +config.attempts; // Convert to number
    if (this.isTruthy(config?.delayMs)) this.retryDelayMs = +config.delayMs; // Convert to number
    if (this.isTruthy(config?.statusCodes)) this.retryStatusCodes = config.statusCodes.split(','); // csvs, spaces not allowed
    if (this.isTruthy(config?.includedRoutes)) this.retryIncludedRoutes = config.includedRoutes.split(','); // csvs, spaces not allowed
    if (this.isTruthy(config?.toastMessage) && config?.toastMessage !== 'hide') this.retryToastMessage = config.toastMessage;
    else if (config?.toastMessage === 'hide') this.retryToastMessage = null; // We need a way to hide the toast altogether
  }

  /**
   * We need a way to "turn off" the retry interceptor for certain requests, but the pipeline vars need some sort of string value.
   * Here, we support "turning off" by using "null" or "false" in the pipeline
   * @param s the input string to check
   * @returns false if s is falsy, true otherwise
   */
  private isTruthy(s: string): boolean {
    return !s || s.toLowerCase() === 'null' || s.toLowerCase() === 'false' ? false : true;
  }
}
